import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, Loader2, Upload } from 'lucide-react';
import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { i18n } from '~/lib/i18n';
import {
  type CandidateProfileResponse,
  candidateProfileQueryOptions
} from '~/queries/candidates.queries';

type CvParsedData = NonNullable<CandidateProfileResponse['cvParsedData']>;

const ACCEPTED_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'image/jpeg',
  'image/png',
  'image/webp'
];
const MAX_SIZE_BYTES = 10 * 1024 * 1024;

const STATUS_VARIANT = {
  pending: 'outline',
  processing: 'secondary',
  done: 'default',
  failed: 'destructive'
} as const;

interface Props {
  orgId: string;
  candidateId: string;
}

export function CandidateProfileTab({ orgId, candidateId }: Props): React.ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: profile, isLoading } = useQuery(candidateProfileQueryOptions(orgId, candidateId));

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      if (!ACCEPTED_TYPES.includes(file.type)) {
        throw new Error(t('candidateProfile.cv.errors.unsupportedType'));
      }
      if (file.size > MAX_SIZE_BYTES) {
        throw new Error(t('candidateProfile.cv.errors.tooLarge'));
      }
      const body = new FormData();
      body.append('file', file);
      const response = await fetch(`/api/org/${orgId}/candidates/${candidateId}/cv`, {
        method: 'POST',
        body
      });
      if (!response.ok) throw new Error(i18n.t('candidateProfile.cv.errors.uploadFailed'));
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['candidate-profile', orgId, candidateId] });
    }
  });

  const handleFile = useCallback(
    (file: File) => {
      uploadMutation.mutate(file);
    },
    [uploadMutation]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const file = e.dataTransfer.files[0];
      if (file) handleFile(file);
    },
    [handleFile]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) handleFile(file);
      e.target.value = '';
    },
    [handleFile]
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-6">
        <Loader2 className="size-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const cvStatus = profile?.cvStatus ?? null;
  const cvParsedData: CvParsedData | null = profile?.cvParsedData ?? null;
  const cvSignedUrl = profile?.cvSignedUrl ?? null;
  const hasCV = cvStatus !== null;

  const cvStatusLabels = {
    pending: t('candidateProfile.cv.status.pending'),
    processing: t('candidateProfile.cv.status.processing'),
    done: t('candidateProfile.cv.status.done'),
    failed: t('candidateProfile.cv.status.failed')
  } as const;

  return (
    <div className="flex flex-col gap-6">
      {/* CV section */}
      {hasCV ? (
        /* Compact row once a CV exists */
        <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-card px-4 py-3">
          <div className="flex items-center gap-2">
            <Badge variant={STATUS_VARIANT[cvStatus]}>
              {cvStatus === 'processing' && <Loader2 className="animate-spin" />}
              {cvStatusLabels[cvStatus]}
            </Badge>
            {cvSignedUrl && (
              <a
                href={cvSignedUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
              >
                <ExternalLink className="size-3.5" />
                {t('candidateProfile.cv.viewOriginal')}
              </a>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            disabled={uploadMutation.isPending}
            onClick={() => fileInputRef.current?.click()}
          >
            {uploadMutation.isPending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <>
                <Upload className="size-4" />
                {t('candidateProfile.cv.replace')}
              </>
            )}
          </Button>
          {uploadMutation.isError && (
            <p className="text-xs text-destructive">{uploadMutation.error.message}</p>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.docx,.jpg,.jpeg,.png,.webp"
            className="hidden"
            onChange={handleInputChange}
          />
        </div>
      ) : (
        /* Full dropzone when no CV yet */
        <div className="rounded-lg border border-border bg-card p-5">
          <h3 className="mb-4 font-semibold">{t('candidateProfile.cv.title')}</h3>
          <div
            onDrop={handleDrop}
            onDragOver={(e) => {
              e.preventDefault();
            }}
            className="flex flex-col items-center gap-3 rounded-lg border-2 border-dashed border-border p-6 text-center transition-colors hover:border-primary/50"
          >
            <Upload className="size-8 text-muted-foreground" />
            <div>
              <p className="text-sm font-medium">{t('candidateProfile.cv.upload')}</p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {t('candidateProfile.cv.uploadLimit')}
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              disabled={uploadMutation.isPending}
              onClick={() => fileInputRef.current?.click()}
            >
              {uploadMutation.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                t('candidateProfile.cv.upload')
              )}
            </Button>
            {uploadMutation.isError && (
              <p className="text-xs text-destructive">{uploadMutation.error.message}</p>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.docx,.jpg,.jpeg,.png,.webp"
            className="hidden"
            onChange={handleInputChange}
          />
        </div>
      )}

      {/* Parsed CV data */}
      {cvParsedData && (
        <div className="flex flex-col gap-4">
          <h3 className="font-semibold">{t('candidateProfile.cv.parsed.title')}</h3>

          {cvParsedData.summary && (
            <p className="text-sm text-muted-foreground">{cvParsedData.summary}</p>
          )}

          {cvParsedData.skills.length > 0 && (
            <div>
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {t('candidateProfile.cv.parsed.skills')}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {cvParsedData.skills.map((skill) => (
                  <Badge key={skill} variant="secondary">
                    {skill}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {cvParsedData.experience.length > 0 && (
            <div>
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {t('candidateProfile.cv.parsed.experience')}
              </p>
              <div className="flex flex-col gap-3">
                {cvParsedData.experience.map((exp, i) => (
                  <div key={i} className="rounded-md border border-border p-3">
                    <p className="font-medium">{exp.role}</p>
                    <p className="text-sm text-muted-foreground">{exp.company}</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {exp.startDate ?? ''}
                      {exp.startDate && ' – '}
                      {exp.endDate ?? t('candidateProfile.cv.parsed.present')}
                    </p>
                    {exp.description && <p className="mt-1.5 text-sm">{exp.description}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {cvParsedData.education.length > 0 && (
            <div>
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {t('candidateProfile.cv.parsed.education')}
              </p>
              <div className="flex flex-col gap-2">
                {cvParsedData.education.map((edu, i) => (
                  <div key={i} className="text-sm">
                    <span className="font-medium">{edu.institution}</span>
                    {edu.degree && <span className="text-muted-foreground"> · {edu.degree}</span>}
                    {edu.field && <span className="text-muted-foreground">, {edu.field}</span>}
                    {edu.year && <span className="text-muted-foreground"> ({edu.year})</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {cvParsedData.languages.length > 0 && (
            <div>
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {t('candidateProfile.cv.parsed.languages')}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {cvParsedData.languages.map((lang) => (
                  <Badge key={lang} variant="outline">
                    {lang}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {cvParsedData.certifications.length > 0 && (
            <div>
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {t('candidateProfile.cv.parsed.certifications')}
              </p>
              <ul className="list-inside list-disc text-sm">
                {cvParsedData.certifications.map((cert) => (
                  <li key={cert}>{cert}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
